import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AddFormComponent,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

import {
  CouponsService,
  CouponIssueCreateRequest,
  CouponIssueResponse,
  CouponIssueUpdateRequest,
  CouponIssuesService,
} from '@app/api/promotion-service';
import { OfferService } from '@app/feature/promotion-management/services/offer.service';
import { COUPON_ISSUE_FORM_FIELDS } from '../coupon-issue.config';
import type { FormField } from '@shared/components/add-form/add-form.component';
import type { IssuedCouponDTO } from '@app/api/promotion-service/model/issuedCouponDTO';
import type {
  PaginationEvent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';

@Component({
  selector: 'app-coupon-add',
  standalone: true,
  imports: [
    CommonModule,
    AddFormComponent,
    ToastModule,
    TranslateModule,
  ],
  templateUrl: './coupon-add.component.html',
  styleUrl: './coupon-add.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class CouponAddComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly destroy$ = new Subject<void>();

  @ViewChild(AddFormComponent) addFormComponent!: AddFormComponent;

  readonly loading = signal(false);
  readonly initialData = signal<any>({});
  /** True when route is `marketing/coupon/edit/:id`. */
  readonly isEditMode = signal(false);
  readonly isViewMode = signal(false);
  readonly issuedCoupons = signal<IssuedCouponDTO[]>([]);
  readonly issuedCouponsLoading = signal(false);
  readonly issuedCouponsTotalRecords = signal(0);
  readonly issuedCouponsCurrentPage = signal(0);
  readonly issuedCouponsPageSize = signal(20);

  private issueId: number | null = null;
  private skipCanDeactivate = false;

  private readonly baseFormFields: FormField[] = COUPON_ISSUE_FORM_FIELDS.map(
    (f) => ({ ...f }),
  );
  formFields: FormField[] = [];

  headerActions = [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      outlined: true,
      severity: 'secondary' as const,
    },
    {
      label: 'common.actions.save',
      action: 'save',
      severity: 'info' as const,
    },
  ];
  issuedCouponColumns: TableColumn[] = [
    {
      prop: 'couponCode',
      label: 'issued_coupon.list.columns.coupon_code',
      sortable: false,
      type: 'text',
      width: 160,
    },
    {
      prop: 'status',
      label: 'issued_coupon.list.columns.status',
      sortable: false,
      type: 'status',
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      prop: 'creationDate',
      label: 'issued_coupon.list.columns.creation_date',
      sortable: false,
      type: 'custom',
      width: 170,
    },
    {
      prop: 'lastUpdateDate',
      label: 'issued_coupon.list.columns.last_update_date',
      sortable: false,
      type: 'custom',
      width: 170,
    },
  ];
  readonly issuedCouponTableSections = signal<TableSection[]>([]);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly couponIssueApi: CouponIssuesService,
    private readonly couponsApi: CouponsService,
    private readonly offerService: OfferService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.issueId = idParam ? Number(idParam) : null;
    this.isEditMode.set(this.issueId != null);
    this.isViewMode.set(this.route.snapshot.routeConfig?.path === 'marketing/coupon/view/:id');

    this.initialData.set({
      couponCode: '',
      couponName: '',
      totalQuantity: null,
      maxPerCustomer: null,
      linkedOfferId: null,
    });

    this.formFields = this.withPaginatedOfferField(this.baseFormFields);

    if (this.issueId) {
      if (this.isViewMode()) {
        this.applyViewModeFieldRules();
        this.headerActions = [
          {
            label: 'common.actions.cancel',
            action: 'cancel',
            outlined: true,
            severity: 'secondary' as const,
          },
        ];
      } else {
      this.applyEditModeFieldRules();
      }
      this.loadCouponIssue(this.issueId);
      this.loadIssuedCoupons(this.issueId);
    }
    this.syncIssuedCouponTableSections();
  }

  private applyViewModeFieldRules(): void {
    // View mode: hide "max per customer", and show the linked offer as its
    // code/name text (resolved in loadCouponIssue) instead of the raw offer id.
    this.formFields = this.baseFormFields.map((f) => {
      if (f.controlName === 'maxPerCustomer') {
        return { ...f, hidden: true };
      }
      if (f.controlName === 'linkedOfferId') {
        return {
          ...f,
          type: 'text',
          options: undefined,
          disabled: true,
          readonly: true,
          required: false,
        };
      }
      return { ...f, disabled: true, readonly: true, required: false };
    });
  }

  private formatOfferDisplay(offer: {
    code?: string;
    displayName?: string;
  }): string {
    const code = offer?.code ?? '';
    const name = offer?.displayName ?? '';
    if (code && name) return `${code} — ${name}`;
    return code || name || '';
  }

  private applyEditModeFieldRules(): void {
    this.formFields = this.withPaginatedOfferField(
      this.baseFormFields.map((f) => {
        if (f.controlName === 'totalQuantity') {
          // PUT /v1/coupon-issues/{id} has no totalIssueQuantity — only show issued count (read-only).
          return {
            ...f,
            label: 'coupon.list.columns.total_issued',
            placeholder: 'coupon.issue.placeholders.total_issued_readonly',
            disabled: true,
            readonly: true,
          };
        }
        if (f.controlName === 'linkedOfferId') {
          return { ...f, required: false };
        }
        return { ...f };
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormAction(action: string, formData: any): void {
    if (this.isViewMode()) {
      if (action === 'cancel') {
        window.history.back();
      }
      return;
    }
    if (action === 'save') {
      this.saveCouponIssue(formData);
    } else if (action === 'cancel') {
      window.history.back();
    }
  }

  // No special cross-field logic for now
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onFormValueChanges(_formData: any): void {}

  private withPaginatedOfferField(fields: FormField[]): FormField[] {
    return withPaginatedFormFields(fields, {
      linkedOfferId: {
        loadPage: (params) => this.offerService.getOffersOptionsPage(params),
      },
    });
  }

  private loadCouponIssue(id: number): void {
    this.loading.set(true);

    this.couponIssueApi
      .get1(id, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (issue: CouponIssueResponse) => {
          if (!issue) return;
          const view = this.isViewMode();
          this.initialData.set({
            couponCode: issue.issueNo ?? '',
            couponName: issue.description ?? '',
            totalQuantity: issue.totalIssuedCount ?? null,
            maxPerCustomer: issue.maxRedemptionsPerCoupon ?? null,
            // Edit mode keeps the numeric id (dropdown); view mode shows code/name text.
            linkedOfferId:
              issue.offerId != null ? (view ? '' : Number(issue.offerId)) : null,
          });
          if (view && issue.offerId != null) {
            this.offerService
              .getOfferById(issue.offerId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (offer) =>
                  this.initialData.set({
                    ...this.initialData(),
                    linkedOfferId: this.formatOfferDisplay(offer),
                  }),
                error: () =>
                  this.initialData.set({
                    ...this.initialData(),
                    linkedOfferId: String(issue.offerId),
                  }),
              });
          }
        },
        error: (error: unknown) => {
          console.error('Failed to load coupon issue:', error);
        },
      });
  }

  private saveCouponIssue(formData: any): void {
    if (this.isViewMode()) {
      return;
    }
    if (!this.issueId && !formData.linkedOfferId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('coupon.messages.error.title'),
        detail: this.translateService.instant(
          'coupon.messages.error.failed_to_create_coupon',
        ),
        life: 6000,
      });
      return;
    }

    this.loading.set(true);

    const request$ = this.issueId
      ? this.couponIssueApi.update1(
          this.issueId,
          this.buildUpdatePayload(formData),
          'body',
          false,
          {
            httpHeaderAccept: 'application/json' as any,
          },
        )
      : this.couponIssueApi.create1(
          this.buildCreatePayload(formData),
          'body',
          false,
          {
            httpHeaderAccept: 'application/json' as any,
          },
        );

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: () => {
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              this.issueId
                ? 'coupon.messages.success.coupon_updated'
                : 'coupon.messages.success.coupon_created',
            ),
            life: 5000,
          });

          setTimeout(() => {
            this.router.navigate(['/marketing/coupon']);
          }, 800);
        },
        error: (error: any) => {
          let errorMessage = this.translateService.instant(
            'coupon.messages.error.failed_to_create_coupon',
          );
          const apiErrorMessage = error?.error?.message || error?.message || '';

          if (apiErrorMessage) {
            errorMessage = apiErrorMessage;
          }

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'coupon.messages.error.title',
            ),
            detail: errorMessage,
            life: 6000,
          });
        },
      });
  }

  canDeactivate(): boolean {
    if (this.isViewMode()) {
      return true;
    }
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private buildCreatePayload(formData: any): CouponIssueCreateRequest {
    const totalQty = formData.totalQuantity;
    return {
      issueNo: formData.couponCode || '',
      description: formData.couponName || '',
      offerId: Number(formData.linkedOfferId),
      maxRedemptionsPerCoupon:
        typeof formData.maxPerCustomer === 'number'
          ? formData.maxPerCustomer
          : undefined,
      totalIssueQuantity:
        typeof totalQty === 'number' && !Number.isNaN(totalQty)
          ? totalQty
          : undefined,
    };
  }

  private buildUpdatePayload(formData: any): CouponIssueUpdateRequest {
    return {
      issueNo: formData.couponCode || '',
      description: formData.couponName || '',
      maxRedemptionsPerCoupon:
        typeof formData.maxPerCustomer === 'number'
          ? formData.maxPerCustomer
          : undefined,
      offerId: formData.linkedOfferId
        ? Number(formData.linkedOfferId)
        : undefined,
    };
  }

  onIssuedCouponsPageChange(event: PaginationEvent): void {
    if (!this.issueId) return;
    if (typeof event.page === 'number') {
      this.issuedCouponsCurrentPage.set(event.page);
    }
    if (typeof event.rows === 'number') {
      this.issuedCouponsPageSize.set(event.rows);
    }
    this.syncIssuedCouponTableSections();
    this.loadIssuedCoupons(this.issueId);
  }

  private loadIssuedCoupons(couponIssueId: number): void {
    this.issuedCouponsLoading.set(true);
    this.syncIssuedCouponTableSections();
    this.couponsApi
      .getCouponsPage(
        {
          page: this.issuedCouponsCurrentPage(),
          size: this.issuedCouponsPageSize(),
          sortBy: 'creationDate',
          ascending: false,
          couponIssueId,
        },
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.issuedCouponsLoading.set(false);
          this.syncIssuedCouponTableSections();
        }),
      )
      .subscribe({
        next: (res) => {
          this.issuedCoupons.set(res?.pageContent ?? []);
          this.issuedCouponsTotalRecords.set(res?.totalElements ?? 0);
          this.syncIssuedCouponTableSections();
        },
        error: () => {
          this.issuedCoupons.set([]);
          this.issuedCouponsTotalRecords.set(0);
          this.syncIssuedCouponTableSections();
        },
      });
  }

  private syncIssuedCouponTableSections(): void {
    this.issuedCouponTableSections.set(
      this.isViewMode()
        ? [
            {
              title: 'coupon.issue.sections.issued_coupon_information',
              name: 'issued_coupon_information',
              show: true,
              useAdvancedTable: true,
              columns: this.issuedCouponColumns,
              defaultColumns: this.issuedCouponColumns,
              flexibleColumns: [],
              data: this.issuedCoupons(),
              loading: this.issuedCouponsLoading(),
              hasCheckbox: false,
              hasSerialColumn: false,
              rows: this.issuedCouponsPageSize(),
              first:
                this.issuedCouponsCurrentPage() * this.issuedCouponsPageSize(),
              totalRecords: this.issuedCouponsTotalRecords(),
              onPageChange: (event: PaginationEvent) =>
                this.onIssuedCouponsPageChange(event),
            },
          ]
        : [],
    );
  }
}
